import { endOfDay, getUnixTime, startOfDay } from 'date-fns';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

import { normalize } from '../../shared/normalizers';

import { FetchStaffTasksOptions, StaffTask, StaffTaskStatus } from './_types';

export * from './_types';
export * from './utils';

export const STAFF_TASKS_QUERY_KEY = 'staffTasks';

export const fetchStaffTasksQueryKey = (options: FetchStaffTasksOptions) => [
  STAFF_TASKS_QUERY_KEY,
  options.page,
  options.perPage,
  options.search,
  options.filters,
  options.assignedToId,
  options.sortBy,
  options.sortDir,
];

export const incompleteStaffTaskCountQueryKey = (userId?: Identifier) => [
  STAFF_TASKS_QUERY_KEY,
  'incompleteCount',
  userId,
];

const dueDateToUnixParams = (dueDate?: { start?: string; end?: string }) => {
  if (!dueDate?.start || !dueDate?.end) return {};

  return {
    start_date: getUnixTime(startOfDay(new Date(dueDate.start))),
    end_date: getUnixTime(endOfDay(new Date(dueDate.end))),
  };
};

const SORT_FIELD_MAP: Record<string, string> = {
  dueAt: 'due_at',
  title: 'title',
  status: 'status',
  regardingLabel: 'regarding_label',
  assignedToName: 'assigned_to_name',
};

export const fetchStaffTasks = async ({
  page = 1,
  perPage = 25,
  search = '',
  filters = {},
  assignedToId,
  sortBy,
  sortDir,
}: FetchStaffTasksOptions): Promise<{ tasks: StaffTask[]; total: number }> => {
  const assigneeIds = filters.assignee
    ? String(filters.assignee)
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    : [];

  const sortField = sortBy ? SORT_FIELD_MAP[sortBy] : undefined;
  const sort = sortField && sortDir ? `${sortField}:${sortDir}` : undefined;

  const response = await api.get('/api/v4/staff/tasks', {
    params: {
      page,
      per: perPage,
      ...(search && { q: { title_cont: search } }),
      ...(filters.status && { status: filters.status }),
      ...(assigneeIds.length > 1 && {
        assigned_to_ids: assigneeIds.join(','),
      }),
      ...(assigneeIds.length === 1 && { assigned_to_id: assigneeIds[0] }),
      ...(assignedToId && { assigned_to_id: assignedToId }),
      ...(filters.regarding?.type &&
        filters.regarding?.id && {
          regarding_type: filters.regarding.type,
          regarding_id: filters.regarding.id,
        }),
      ...dueDateToUnixParams(filters.dueDate),
      ...(sort && { sort }),
    },
  });

  const tasks = parseStaffTasksResponse(response.data);

  return {
    tasks,
    total: response.data?.meta?.total ?? tasks.length,
  };
};

const parseStaffTasksResponse = (data: unknown): StaffTask[] => {
  if (!data || typeof data !== 'object') return [];

  const payload = (data as { task?: unknown }).task ?? data;

  if (Array.isArray(payload)) {
    return payload.map((task) => normalize<StaffTask>(task));
  }

  if (payload && typeof payload === 'object') {
    return [normalize<StaffTask>(payload)];
  }

  return [];
};

export type StaffTaskInput = Partial<{
  title: string;
  description: string;
  status: StaffTaskStatus;
  assignedToId: Identifier;
  dueAt: number;
  regardingMemberId: Identifier | null;
  regardingStaffId: Identifier | null;
}>;

export const fetchIncompleteStaffTaskCount = async (): Promise<number> => {
  const response = await api.get('/api/v4/staff/tasks/incomplete_count');
  const count = response.data?.count;

  return typeof count === 'number' ? count : Number(count) || 0;
};

export const createStaffTask = async (
  task: StaffTaskInput
): Promise<StaffTask> => {
  const response = await api.post('/api/v4/staff/tasks', {
    task: decamelizeKeys(task, { deep: true }),
  });

  return normalize<StaffTask>(response.data);
};

export const updateStaffTask = async (
  id: Identifier,
  task: StaffTaskInput
): Promise<StaffTask> => {
  const response = await api.put(`/api/v4/staff/tasks/${id}`, {
    task: decamelizeKeys(task, { deep: true }),
  });

  return normalize<StaffTask>(response.data);
};

export const deleteStaffTask = async (id: Identifier): Promise<void> => {
  await api.delete(`/api/v4/staff/tasks/${id}`);
};
